import type { GetTokenSilentlyVerboseResponse } from "@auth0/auth0-spa-js";
import { useGraphQLClient } from "@package/graphql/react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useAuthUser } from "@package/react/auth";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { logger } from "@artis/logger";
import dayjs from "dayjs";

import { parseNumber } from "../../webapp/numbers";
import { ORG_BY_ID_QUERY } from "../../data/org";
import { getIsLoggingOut } from "./logoutState";
import type { JWT } from "../../globals";

export const useUserId = () => useAuthUser().id;

const useAccessTokenQueryOptions = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  return queryOptions({
    queryKey: ["auth", user?.sub ?? "_", "access_token"],
    queryFn: async (): Promise<GetTokenSilentlyVerboseResponse> => {
      if (getIsLoggingOut()) {
        throw new Error("logout_in_progress");
      }
      if (!navigator.onLine) {
        logger.info("Access token: offline, waiting for network before refresh", { tags: ["auth"] });
        await waitForOnline();
        logger.info("Access token: back online, proceeding with refresh", { tags: ["auth"] });
      }

      let lastErr: unknown;
      for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
        try {
          // Soft refresh: SDK returns cached token if still valid.
          let result = await getAccessTokenSilently({
            detailedResponse: true,
            authorizationParams: { audience: "hasura" },
          });
          const expirations = getAuth0TokenExpirations(result);
          if (expirations.soonest) {
            logger.withGlobalFields({ tokenExpiresAt: expirations.soonest.toISOString() });
          }
          let claims = jwtDecode<JWT>(result.access_token);
          const now = Math.floor(Date.now() / 1000);
          const secsLeft = expirations.soonest
            ? expirations.soonest.diff(dayjs(), "seconds")
            : typeof claims.exp === "number"
              ? claims.exp - now
              : 0;

          // Hard refresh: cached token is within the refresh window, force a real fetch.
          if (secsLeft < HARD_REFRESH_WINDOW_SEC) {
            logger.info("Access token: within refresh window, forcing fresh fetch", {
              tags: ["auth"],
              secs_left: secsLeft,
              hard_refresh_window_sec: HARD_REFRESH_WINDOW_SEC,
            });
            result = await getAccessTokenSilently({
              detailedResponse: true,
              cacheMode: "off",
              authorizationParams: { audience: "hasura" },
            });
            claims = jwtDecode<JWT>(result.access_token);
          }

          logger.info("Access token: refresh succeeded", {
            tags: ["auth"],
            attempt,
            exp: claims.exp,
            iat: claims.iat,
            secs_to_exp: typeof claims.exp === "number" ? claims.exp - now : null,
          });
          return result;
        } catch (err) {
          lastErr = err;
          const code = (err as { error?: unknown })?.error;
          const msg = (err as { message?: unknown })?.message;
          logger.warn("Access token: refresh attempt failed", {
            tags: ["auth"],
            attempt,
            max_attempts: MAX_REFRESH_ATTEMPTS,
            error_code: typeof code === "string" ? code : "unknown",
            message: typeof msg === "string" ? msg : undefined,
          });
          if (attempt < MAX_REFRESH_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
          }
        }
      }
      throw lastErr;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
};

export const useAccessToken = () => useQuery(useAccessTokenQueryOptions());

const waitForOnline = (): Promise<void> => {
  if (navigator.onLine) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const handler = () => {
      window.removeEventListener("online", handler);
      resolve();
    };
    window.addEventListener("online", handler);
    // Race guard: navigator.onLine may have flipped between the check above and adding the listener.
    if (navigator.onLine) {
      window.removeEventListener("online", handler);
      resolve();
    }
  });
};

// navigator.onLine isn't fully trustworthy: per MDN it only reflects LAN
// connectivity, not actual internet reachability. Retry a few times so a
// momentarily-flaky network on wake doesn't immediately bounce the user to login.
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_RETRY_DELAY_MS = 1000;

// Hard refresh token before it expires (expiry - HARD_REFRESH_WINDOW_SEC).
// Exported for tests; the production value is 3600 (1h).
export const HARD_REFRESH_WINDOW_SEC = 3600;

// How often the watchdog polls. Each tick only compares timestamps so it is cheap; short so a resume after sleep is noticed within ~1s. Exported for tests; production = 1 (1s).
export const POLL_TICK_SEC = 1;

// After a failed refetch, retry this soon instead of waiting a full TOKEN_REFRESH_INTERVAL_SEC. Exported for tests; production = 30 (30s).
export const RETRY_AFTER_FAILURE_SEC = 30;

// Refetch the access token once this long has passed since the last refresh. The poll enforces it within POLL_TICK_SEC, including across a sleep. Exported for tests; production = 300 (5min).
export const TOKEN_REFRESH_INTERVAL_SEC = 300;

// Mount ONCE near the auth root. Polls every POLL_TICK_SEC and refetches the shared access-token query once TOKEN_REFRESH_INTERVAL_SEC has elapsed since the last refresh, so the cadence survives a sleep. The queryFn handles the decode + conditional hard refresh.
export const useTokenRefreshWatchdog = () => {
  const qry = useQuery(useAccessTokenQueryOptions());

  const refetchRef = useRef(qry.refetch);
  refetchRef.current = qry.refetch;

  useEffect(() => {
    logger.info("Token watchdog: mounted", {
      tags: ["auth"],
      poll_sec: POLL_TICK_SEC,
      refresh_sec: TOKEN_REFRESH_INTERVAL_SEC,
    });
    let lastRefreshedAt = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      const gapSec = (now - lastRefreshedAt) / 1000;
      if (gapSec < TOKEN_REFRESH_INTERVAL_SEC) return;
      lastRefreshedAt = now;
      logger.info("Token watchdog: tick", { tags: ["auth"], gap_sec: Math.round(gapSec) });
      refetchRef.current().then((res) => {
        if (res.isError) {
          lastRefreshedAt = now - (TOKEN_REFRESH_INTERVAL_SEC - RETRY_AFTER_FAILURE_SEC) * 1000;
        }
      });
    }, POLL_TICK_SEC * 1000);
    return () => {
      clearInterval(id);
      logger.info("Token watchdog: unmounted", { tags: ["auth"] });
    };
  }, []);

  return qry;
};

export const useIdToken = () =>
  useQuery({
    ...useAccessTokenQueryOptions(),
    select: (data) => jwtDecode<JWT>(data.id_token),
  });

export const useClaims = () =>
  useQuery({
    ...useAccessTokenQueryOptions(),
    select: (data) => jwtDecode<JWT>(data.access_token),
  });

export type TSession =
  | {
      session_id: string;
      access_token: string;
      session_start: string;
    }
  | null
  | undefined;

export const useSessionState = () =>
  useQuery({
    ...useAccessTokenQueryOptions(),
    select: ({ access_token, id_token }) => {
      const parsedToken = jwtDecode<JWT>(id_token);
      const session_id = parsedToken.sid;
      const session_start = new Date(parsedToken.iat * 1000).toISOString();
      return {
        access_token,
        session_id,
        session_start,
      };
    },
  });

export function useCurrentUserOrg() {
  const graphql = useGraphQLClient();
  const claims = useClaims();
  const orgId = parseNumber(claims.data?.["https://hasura.io/jwt/claims"]?.["x-hasura-org-id"]);
  return useQuery({
    enabled: !!orgId,
    queryKey: [ORG_BY_ID_QUERY],
    queryFn: () => graphql.execute(ORG_BY_ID_QUERY, { id: orgId ?? 7 }),
  });
}

export function useCurrentUsername() {
  const { user } = useAuth0();
  return `${user?.given_name} ${user?.family_name}`;
}

export function useRoles() {
  const claims = useClaims();

  return useMemo(() => {
    if (!claims.data) {
      return [];
    }

    const claimsRoles = claims.data?.["https://hasura.io/jwt/claims"]?.["x-hasura-allowed-roles"] || [];
    if (claimsRoles.includes("umi-internal-write")) {
      return ["umi-internal-write", "umi-internal-read"];
    }
    return claimsRoles;
  }, [claims.data]);
}

export function useParseToken(token: string | undefined) {
  return useMemo(() => {
    if (!token) return null;
    return jwtDecode<JWT>(token);
  }, [token]);
}

type Auth0TokenExpirations = {
  access_token?: dayjs.Dayjs;
  id_token?: dayjs.Dayjs;
  expires_in?: dayjs.Dayjs;
  soonest?: dayjs.Dayjs;
};

const safeJwtExpiry = (token: unknown): dayjs.Dayjs | undefined => {
  if (typeof token !== "string" || token.length === 0) return undefined;
  try {
    const exp = jwtDecode(token).exp;
    if (typeof exp !== "number" || !Number.isFinite(exp)) return undefined;
    return dayjs(exp * 1000);
  } catch {
    return undefined;
  }
};

export const getAuth0TokenExpirations = (
  p: Partial<GetTokenSilentlyVerboseResponse> | null | undefined,
): Auth0TokenExpirations => {
  const out: Auth0TokenExpirations = {};
  if (!p) return out;

  if (p.access_token) {
    out.access_token = safeJwtExpiry(p.access_token);
  }
  if (p.id_token) {
    out.id_token = safeJwtExpiry(p.id_token);
  }
  if (typeof p.expires_in === "number" && Number.isFinite(p.expires_in) && p.expires_in > 0) {
    out.expires_in = dayjs().add(p.expires_in, "seconds");
  }

  for (const candidate of [out.access_token, out.id_token, out.expires_in]) {
    if (!candidate) continue;
    if (!out.soonest || candidate.isBefore(out.soonest)) out.soonest = candidate;
  }

  return out;
};
